import type {FilterConfiguration} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import type {OrganizationalUnit} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {getOuIdsFromUser} from "~/utilities/accessControl/ouHierarchyDisplay";

/**
 * Builds a map of OU template id -> depth in hierarchy (root = 0).
 * Assumes templates form a valid tree (parentId references another template or null).
 */
function getOuTemplateDepthMap(
    organizationalUnitTemplates: Array<OrganizationalUnitTemplate>,
): Map<Uuid, number> {
    const map = new Map<Uuid, number>();

    function depth(templateId: Uuid): number {
        const cached = map.get(templateId);
        if (cached !== undefined) {
            return cached;
        }
        const template = organizationalUnitTemplates.find((t) => t.id === templateId);
        if (template === undefined) {
            return 0;
        }
        if (template.parentId === null) {
            map.set(templateId, 0);
            return 0;
        }
        const d = 1 + depth(template.parentId);
        map.set(templateId, d);
        return d;
    }

    for (const t of organizationalUnitTemplates) {
        depth(t.id);
    }
    return map;
}

/**
 * Returns the list of ancestor OU ids from the given OU up to root (inclusive).
 * First element is the given ouId, then parent, then grandparent, etc.
 */
function getAncestorOuIds(
    ouId: Uuid,
    organizationalUnits: Array<OrganizationalUnit>,
): Array<Uuid> {
    const result: Array<Uuid> = [];
    let currentId: Uuid | null = ouId;
    while (currentId !== null) {
        result.push(currentId);
        const ou = organizationalUnits.find((u) => u.id === currentId);
        if (ou === undefined) {
            break;
        }
        currentId = ou.parentId;
    }
    return result;
}

/**
 * Finds the ancestor of the given OU (walking up toward root) whose template
 * matches the given templateId. Returns that OU's id, or null if none found.
 */
function findAncestorWithTemplate(
    startOuId: Uuid,
    templateId: Uuid,
    organizationalUnits: Array<OrganizationalUnit>,
): Uuid | null {
    const ancestorIds = getAncestorOuIds(startOuId, organizationalUnits);
    for (const id of ancestorIds) {
        const ou = organizationalUnits.find((u) => u.id === id);
        if (ou !== undefined && ou.templateId === templateId) {
            return ou.id;
        }
    }
    return null;
}

function isUnrestrictedOrganizationalUnitTemplateId(templateId: unknown): templateId is null | undefined {
    return templateId === undefined || templateId === null;
}

export type UserOuAccessParams = {
    targetUser: User;
    users: Array<User>;
    userTemplates: Array<UserTemplate>;
    organizationalUnits: Array<OrganizationalUnit>;
    organizationalUnitTemplates: Array<OrganizationalUnitTemplate>;
    targetOrganisationalUnitId: Uuid;
};

/**
 * Determines whether the target user has access to the target organisational unit.
 *
 * Rules:
 * - If the user's template has no organisationalUnitTemplateId (e.g. top-level role),
 *   the user has access to any OU (always true).
 * - If the target OU's template is "above" the user's mapped OU type in the OU hierarchy
 *   (e.g. target is type A, user is mapped to type C), the user cannot have access (false).
 * - Otherwise, we traverse from the target OU up to find an ancestor OU whose type
 *   matches the user's mapped type. The user has access if their designated OU is
 *   that ancestor or a descendant of that ancestor (i.e. that ancestor is on the path
 *   from the user's designated OU to root).
 */
export function userHasAccessToOrganisationalUnit(params: UserOuAccessParams): boolean {
    const {
        targetUser,
        userTemplates,
        organizationalUnits,
        organizationalUnitTemplates,
        targetOrganisationalUnitId,
    } = params;

    const userTemplate = userTemplates.find((t) => t.id === targetUser.templateId);
    if (userTemplate === undefined) {
        return false;
    }

    const mappedOuTemplateId = userTemplate.configuration.organizationalUnitTemplateId;
    if (isUnrestrictedOrganizationalUnitTemplateId(mappedOuTemplateId)) {
        return true;
    }

    const targetOu = organizationalUnits.find((ou) => ou.id === targetOrganisationalUnitId);
    if (targetOu === undefined) {
        return false;
    }

    const depthMap = getOuTemplateDepthMap(organizationalUnitTemplates);
    const targetOuDepth = depthMap.get(targetOu.templateId) ?? 0;
    const mappedOuDepth = depthMap.get(mappedOuTemplateId) ?? 0;

    if (targetOuDepth < mappedOuDepth) {
        return false;
    }

    const ancestorOuId = findAncestorWithTemplate(
        targetOrganisationalUnitId,
        mappedOuTemplateId,
        organizationalUnits,
    );
    if (ancestorOuId === null) {
        return false;
    }

    const ouColumnId = userTemplate.configuration.organizationalUnitColumnId;
    const designatedOuIds = getOuIdsFromUser(targetUser, ouColumnId);
    const designatedOuId = designatedOuIds.length > 0 ? designatedOuIds[0] ?? null : null;
    if (designatedOuId === null) {
        return false;
    }

    const userOuAncestorIds = new Set(getAncestorOuIds(designatedOuId, organizationalUnits));
    return userOuAncestorIds.has(ancestorOuId);
}

const OU_ACCESS_SCOPE_FILTER_COLUMN_ID = "id";

/**
 * Returns organisational unit ids the user may access, or `null` when unrestricted
 * (user template has no `organizationalUnitTemplateId`).
 */
export function collectOrganisationalUnitIdsAccessibleToUser(params: {
    targetUser: User;
    users: Array<User>;
    userTemplates: Array<UserTemplate>;
    organizationalUnits: Array<OrganizationalUnit>;
    organizationalUnitTemplates: Array<OrganizationalUnitTemplate>;
    /** When set, only these OUs are considered (e.g. dealership template rows). */
    candidateOrganisationalUnits?: Array<OrganizationalUnit>;
}): null | Array<Uuid> {
    const {
        targetUser,
        users,
        userTemplates,
        organizationalUnits,
        organizationalUnitTemplates,
        candidateOrganisationalUnits,
    } = params;

    const userTemplate = userTemplates.find((template) => template.id === targetUser.templateId);
    if (userTemplate === undefined) {
        return [];
    }
    if (isUnrestrictedOrganizationalUnitTemplateId(userTemplate.configuration.organizationalUnitTemplateId)) {
        return null;
    }

    const pool = candidateOrganisationalUnits ?? organizationalUnits;
    const accessibleOrganisationalUnitIds: Array<Uuid> = [];
    for (const organisationalUnit of pool) {
        if (
            userHasAccessToOrganisationalUnit({
                targetUser: targetUser,
                users: users,
                userTemplates: userTemplates,
                organizationalUnits: organizationalUnits,
                organizationalUnitTemplates: organizationalUnitTemplates,
                targetOrganisationalUnitId: organisationalUnit.id,
            }) === true
        ) {
            accessibleOrganisationalUnitIds.push(organisationalUnit.id);
        }
    }
    return accessibleOrganisationalUnitIds;
}

/**
 * Adds a backend `id in (...)` filter for OU UACM scope. Pass `null` for unrestricted access (no change).
 * Returns `null` when the accessible set is empty so callers can skip the API and return zero rows.
 */
export function filterConfigurationWithOrganisationalUnitAccessScope(
    filterConfiguration: FilterConfiguration,
    accessibleOrganisationalUnitIds: null | Array<Uuid>,
): FilterConfiguration | null {
    if (accessibleOrganisationalUnitIds === null) {
        return filterConfiguration;
    }
    if (accessibleOrganisationalUnitIds.length === 0) {
        return null;
    }

    const lineItemsWithoutOuIdScope = filterConfiguration.lineItems.filter(
        (lineItem) => lineItem.columnId !== OU_ACCESS_SCOPE_FILTER_COLUMN_ID,
    );

    return {
        revisionId: filterConfiguration.revisionId,
        lineItems: [
            ...lineItemsWithoutOuIdScope,
            {
                columnId: OU_ACCESS_SCOPE_FILTER_COLUMN_ID,
                operation: FilterOperation.in,
                values: accessibleOrganisationalUnitIds.map((organisationalUnitId) => String(organisationalUnitId)),
            },
        ],
    };
}
